import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { Box, styled } from '@mui/material';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import InfoList from '@/components/Approval/@components/InfoList';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  SalesBrandItem,
  SalesBrandList,
} from '@/components/Approval/@components/SalesBrandPermission';
import { getSalesBrandPermissionDraftSourceUrl } from '@/constant/approval';
import { MyInfoState } from '@/recoil/atom';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  applySavedChanges,
  buildSalesBrandPermissionSubject,
  getSalesBrandChanges,
  salesBrandPermissionKey,
  SALES_BRAND_LOAD_FAILED_MESSAGE,
  toSalesBrands,
  type SalesBrandDraftSource,
} from '@/utils/approval/salesBrandPermission';
import {
  toVehicleCostEmployee,
  VEHICLE_COST_HR_MISSING_MESSAGE,
} from '@/utils/approval/vehicleCost';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const LoadFailedText = styled('p')`
  margin: 0;
  font-size: 14px;
  color: var(--dk-text-secondary, #888d96);
`;

/** Sales 브랜드 권한 변경 신청(215) — 레거시 appr_edit_forms_sales_brand_permission_request */
const SalesBrandPermissionField = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'salesBrandPermission' });
  const myInfo = useAtomValue(MyInfoState);
  const employee = useMemo(() => toVehicleCostEmployee(myInfo), [myInfo]);

  const [loadFailed, setLoadFailed] = useState(false);

  // 체크 기준은 저장값이 아니라 지금 보유 권한 — 캐시 없이 진입마다 다시 받음
  useEffect(() => {
    if (!employee) return undefined;
    let cancelled = false;
    getNestedDataFetcher<SalesBrandDraftSource>(
      getSalesBrandPermissionDraftSourceUrl(),
    ).then(
      (data) => {
        if (cancelled) return;
        // 목록이 없는 응답은 실패, 빈 배열은 레거시처럼 빈 목록
        if (!Array.isArray(data?.display?.brands)) {
          setLoadFailed(true);
          return;
        }
        const current = getValues('salesBrandPermission');
        if (!current || current.brands) return;
        const brands = toSalesBrands(data);
        const next = {
          ...current,
          brands,
          checked: applySavedChanges(brands, current.saved),
        };
        setValue('salesBrandPermission', next);
        // 그 사이 권한이 바뀌어 변경분이 달라졌을 때만 제목을 다시 씀
        if (
          current.saved &&
          salesBrandPermissionKey(next) !==
            salesBrandPermissionKey({ ...current, brands: null })
        ) {
          setValue(
            'subject',
            buildSalesBrandPermissionSubject(
              employee.name,
              getSalesBrandChanges(next),
            ),
          );
        }
      },
      // 받아오기 실패만 안내 — 초기화 중 예외는 그대로 드러나게
      () => {
        if (!cancelled) setLoadFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [employee, getValues, setValue]);

  if (!employee || !state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  const handleToggle = (code: string, checked: boolean) => {
    const current = getValues('salesBrandPermission');
    if (!current?.brands) return;
    const next = {
      ...current,
      checked: checked
        ? [...current.checked, code]
        : current.checked.filter((c) => c !== code),
    };
    setValue('salesBrandPermission', next, { shouldDirty: true });
    // 레거시 — 체크할 때마다 직접 고친 제목도 덮어씀
    setValue(
      'subject',
      buildSalesBrandPermissionSubject(
        employee.name,
        getSalesBrandChanges(next),
      ),
    );
  };

  const renderBrands = () => {
    if (loadFailed && !state.brands) {
      return (
        <LoadFailedText>{t(SALES_BRAND_LOAD_FAILED_MESSAGE)}</LoadFailedText>
      );
    }
    if (!state.brands) {
      return (
        <Box py={1}>
          <Spinner theme="black" size="20px" />
        </Box>
      );
    }
    return (
      <SalesBrandList>
        {state.brands.map((brand) => (
          <SalesBrandItem
            key={brand.code}
            brand={brand}
            checked={state.checked.includes(brand.code)}
            onChange={(checked) => handleToggle(brand.code, checked)}
          />
        ))}
      </SalesBrandList>
    );
  };

  return (
    <>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('신청자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <InfoList
          rows={[
            { label: t('이름'), value: employee.name },
            { label: t('사번'), value: employee.employeeNumber },
            { label: t('소속'), value: employee.affiliation },
            { label: t('입사일'), value: employee.enterDate },
          ]}
        />
      </FieldBlock>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('Sales 브랜드 조회 권한')}</InfoSectionTitle>
        </FieldHeader>
        {renderBrands()}
      </FieldBlock>
    </>
  );
};

export default SalesBrandPermissionField;
